import React, { useState, useEffect } from "react";
import axiosInstance from "../../api/axios";
import { useSelector } from "react-redux";
import Pagination from "../../components/Pagination";
import SortableTableHeader from "../../components/SortableTableHeader";
import { X } from "lucide-react";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
const labelCls =
  "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

const SalesReport = () => {
  const token = useSelector((state) => state.auth.token);
  const [filters, setFilters] = useState({
    username: "",
    status: "",
    startDate: "",
    endDate: "",
    order: "desc",
    orderBy: "totalSale",
    page: 1,
    pageSize: 15,
  });
  const [tempFilters, setTempFilters] = useState({
    username: "",
    status: "",
    startDate: "",
    endDate: "",
    order: "desc",
    orderBy: "totalSale",
    page: 1,
    pageSize: 15,
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [sortedData, setSortedData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSalesModal, setShowSalesModal] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => { if (value) params[key] = value; });
      if (!params.order) params.order = "desc";
      if (!params.orderBy) params.orderBy = "totalSale";
      const res = await axiosInstance.get("/api/admin/reports/sales-report", { params, headers: { token } });
      const newData = res.data.data.data || [];
      setData(newData);
      setSortedData(newData);
      setPagination({
        currentPage: Number(filters.page) || 1,
        totalPages: res.data.data.totalPages || 1,
        totalRows: res.data.data.totalRows || 0,
      });
    } catch (err) {
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key];
      let bValue = b[sort.key];
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";
      if (sort.key === "totalSales") {
        aValue = calculateTotalSales(a.salesReport);
        bValue = calculateTotalSales(b.salesReport);
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === "directSales") {
        aValue = calculateDirectSales(a.salesReport);
        bValue = calculateDirectSales(b.salesReport);
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === "indirectSales") {
        aValue = calculateIndirectSales(a.salesReport);
        bValue = calculateIndirectSales(b.salesReport);
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === "createdAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (e) => {
    setTempFilters({ ...tempFilters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => setFilters({ ...tempFilters });

  const clearFilters = () => {
    const d = { username: "", startDate: "", endDate: "", order: "desc", page: 1, pageSize: 10 };
    setTempFilters(d);
    setFilters(d);
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (newPage) => setFilters((prev) => ({ ...prev, page: newPage }));

  const filterByToday = () => {
    const today = new Date().toISOString().split("T")[0];
    setTempFilters((prev) => ({ ...prev, startDate: today, endDate: today, page: 1 }));
  };

  const filterByWeek = () => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 6);
    setTempFilters((prev) => ({ ...prev, startDate: weekAgo.toISOString().split("T")[0], endDate: today.toISOString().split("T")[0], page: 1 }));
  };

  const handleViewSales = (user) => { setSelectedUser(user); setShowSalesModal(true); };
  const closeSalesModal = () => { setShowSalesModal(false); setSelectedUser(null); };

  const calculateTotalSales = (salesReport) => salesReport?.totalSale || 0;
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return "$0.00";
    return `$${Number(amount).toFixed(2)}`;
  };
  const calculateDirectSales = (salesReport) => salesReport?.direct?.total || 0;
  const calculateIndirectSales = (salesReport) => salesReport?.inDirect?.total || 0;
  const getTotalGroups = (salesReport) => salesReport?.totalGroups || 0;

  const exportToExcel = async () => {
    try {
      const params = {};
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.order) params.order = filters.order;
      if (filters.orderBy) params.orderBy = filters.orderBy;
      params.pageSize = 10000;
      params.page = 1;
      const res = await axiosInstance.get("/api/admin/reports/sales-report", { params, headers: { token } });
      const exportData = res.data.data.data;
      if (!exportData.length) { alert("No data to export"); return; }
      const headers = ["Name", "Surname", "Username", "Email", "Role", "Status", "Total Sales", "Direct Sales", "Indirect Sales", "Groups", "Created At"];
      const csvContent = [
        headers.join(","),
        ...exportData.map((row) =>
          [row.name || "", row.surname || "", row.username || "", row.email || "", row.role || "", row.accountStatus || "", formatCurrency(calculateTotalSales(row.salesReport)), calculateDirectSales(row.salesReport) || "", formatCurrency(calculateIndirectSales(row.salesReport)), getTotalGroups(row.salesReport) || "", new Date(row.createdAt).toLocaleString()].join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", `sales_report_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert("Export failed. Please try again.");
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Reports
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Sales <span className="text-gradient-ginox">Report</span>
      </h1>

      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Username</label>
            <input name="username" value={tempFilters.username} onChange={handleTempFilterChange} placeholder="Username" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" name="startDate" value={tempFilters.startDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input type="date" name="endDate" value={tempFilters.endDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Order</label>
            <select name="order" value={tempFilters.order} onChange={handleTempFilterChange} className={inputCls}>
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select name="pageSize" value={tempFilters.pageSize} onChange={handleTempFilterChange} className={inputCls}>
              {[10, 15, 20, 25, 30, 50].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button type="button" onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
          <button type="button" onClick={filterByToday} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Today
          </button>
          <button type="button" onClick={filterByWeek} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            This Week
          </button>
          <button type="button" onClick={exportToExcel} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort} className="text-left">S.No</SortableTableHeader>
              <SortableTableHeader sortKey="name" currentSort={sort} onSort={handleSort} className="text-left">Name</SortableTableHeader>
              <SortableTableHeader sortKey="username" currentSort={sort} onSort={handleSort} className="text-left">Username</SortableTableHeader>
              <SortableTableHeader sortKey="email" currentSort={sort} onSort={handleSort} className="text-left">Email</SortableTableHeader>
              <SortableTableHeader sortKey="role" currentSort={sort} onSort={handleSort} className="text-left">Role</SortableTableHeader>
              <SortableTableHeader sortKey="accountStatus" currentSort={sort} onSort={handleSort} className="text-left">Status</SortableTableHeader>
              <SortableTableHeader sortKey="totalSales" currentSort={sort} onSort={handleSort} className="text-left">Total Sales</SortableTableHeader>
              <SortableTableHeader sortKey="directSales" currentSort={sort} onSort={handleSort} className="text-left">Direct Sales</SortableTableHeader>
              <SortableTableHeader sortKey="indirectSales" currentSort={sort} onSort={handleSort} className="text-left">Indirect Sales</SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-left">Groups</SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-left">Actions</SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
            ) : sortedData.length === 0 ? (
              <tr><td colSpan={11} className="py-12 text-center text-sm text-muted-foreground">No data found</td></tr>
            ) : (
              sortedData.map((item, index) => (
                <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{(filters.page - 1) * filters.pageSize + index + 1}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.name} {item.surname}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.username}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.email}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.role}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${item.accountStatus === "active" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-red-500/30 bg-red-500/10 text-red-400"}`}>
                      {item.accountStatus}
                    </span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatCurrency(calculateTotalSales(item.salesReport))}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{calculateDirectSales(item.salesReport)}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatCurrency(calculateIndirectSales(item.salesReport))}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{getTotalGroups(item.salesReport)}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    <button
                      onClick={() => handleViewSales(item)}
                      className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      View Sales
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalRows={pagination.totalRows}
        pageSize={filters.pageSize}
        onPageChange={handlePageChange}
      />

      {/* Sales Modal */}
      {showSalesModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto relative">
            <button
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              onClick={closeSalesModal}
            >
              <X size={18} />
            </button>
            <h3 className="text-base font-medium text-foreground mb-1">
              Sales Details — {selectedUser.name} {selectedUser.surname}
            </h3>
            <p className={`${labelCls} mb-4`}>Username: {selectedUser.username}</p>

            {selectedUser.salesReport ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Sales Summary</h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Total Groups:</span><span className="font-mono text-foreground">{selectedUser.salesReport.totalGroups || 0}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Total Sales:</span><span className="font-mono text-foreground">{formatCurrency(calculateTotalSales(selectedUser.salesReport))}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Direct Sales:</span><span className="font-mono text-foreground">{calculateDirectSales(selectedUser.salesReport)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Indirect Sales:</span><span className="font-mono text-foreground">{formatCurrency(calculateIndirectSales(selectedUser.salesReport))}</span></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Direct Sales Breakdown</h4>
                  {selectedUser.salesReport.direct && (
                    <div className="rounded-lg border border-border/40 bg-white/[0.02] p-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-muted-foreground">VPN: <span className="font-mono text-foreground">{selectedUser.salesReport.direct.VPN || 0}</span></div>
                        <div className="text-muted-foreground">Signal Channel: <span className="font-mono text-foreground">{selectedUser.salesReport.direct.signalChannel || 0}</span></div>
                        <div className="text-muted-foreground">Service: <span className="font-mono text-foreground">{selectedUser.salesReport.direct.service || 0}</span></div>
                        <div className="text-muted-foreground">Box: <span className="font-mono text-foreground">{selectedUser.salesReport.direct.box || 0}</span></div>
                        <div className="text-muted-foreground">License: <span className="font-mono text-foreground">{selectedUser.salesReport.direct.license || 0}</span></div>
                        <div className="text-muted-foreground">Total: <span className="font-mono text-foreground">{selectedUser.salesReport.direct.total || 0}</span></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Indirect Sales Breakdown</h4>
                  {selectedUser.salesReport.inDirect && (
                    <div className="rounded-lg border border-border/40 bg-white/[0.02] p-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-muted-foreground">VPN: <span className="font-mono text-foreground">{formatCurrency(selectedUser.salesReport.inDirect.VPN || 0)}</span></div>
                        <div className="text-muted-foreground">Signal Channel: <span className="font-mono text-foreground">{formatCurrency(selectedUser.salesReport.inDirect.signalChannel || 0)}</span></div>
                        <div className="text-muted-foreground">Service: <span className="font-mono text-foreground">{formatCurrency(selectedUser.salesReport.inDirect.service || 0)}</span></div>
                        <div className="text-muted-foreground">Box: <span className="font-mono text-foreground">{formatCurrency(selectedUser.salesReport.inDirect.box || 0)}</span></div>
                        <div className="text-muted-foreground">License: <span className="font-mono text-foreground">{formatCurrency(selectedUser.salesReport.inDirect.license || 0)}</span></div>
                        <div className="text-muted-foreground">Total: <span className="font-mono text-foreground">{formatCurrency(selectedUser.salesReport.inDirect.total || 0)}</span></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Group Details</h4>
                  {selectedUser.salesReport.groups && selectedUser.salesReport.groups.map((group, index) => (
                    <div key={index} className="rounded-lg border border-border/40 bg-white/[0.02] p-3">
                      <div className="text-sm font-medium text-foreground mb-2">Group: {group.group}</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-muted-foreground">Direct Sales: <span className="font-mono text-foreground">{group.directSales?.totalSales || 0}</span></div>
                        <div className="text-muted-foreground">Indirect Sales: <span className="font-mono text-foreground">{formatCurrency(group.inDirectSales?.totalSales || 0)}</span></div>
                        <div className="text-muted-foreground">Total: <span className="font-mono text-foreground">{formatCurrency(group.totalSales || 0)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">No sales report found for this user.</div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeSalesModal}
                className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesReport;
